import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateOrderFormField, clearOrderFormError } from '../../redux_files/actions/orderFormActions';
import { THEME } from '../../utils/theme';
import { DINNER_CUTOFF_MINUTES } from '../../utils/constants';

const DeliveryDateStep = () => {
  const dispatch = useDispatch();
  const { selectedDate } = useSelector(state => state.orderForm);
  const dateError = useSelector(state => state.orderForm.errors.date);

  const today = new Date();
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
  const now = new Date();
  const minutesNow = now.getHours() * 60 + now.getMinutes();
  const cutoffMinutes = DINNER_CUTOFF_MINUTES; // 6:30 PM
  const isAfterCutoff = minutesNow >= cutoffMinutes;

  const pad = (n) => String(n).padStart(2, '0');
  const formatYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  // Normalize any incoming selectedDate to YYYY-MM-DD for reliable comparisons
  const normalizeToYMD = (val) => {
    if (!val) return null;
    if (val instanceof Date) return formatYMD(val);
    if (typeof val === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
      const parsed = new Date(val);
      return isNaN(parsed) ? null : formatYMD(parsed);
    }
    const parsed = new Date(val);
    return isNaN(parsed) ? null : formatYMD(parsed);
  };

  const options = [
    { key: 'today', label: 'Today', value: formatYMD(today), subtitle: today.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' }) },
    { key: 'tomorrow', label: 'Tomorrow', value: formatYMD(tomorrow), subtitle: tomorrow.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' }) }
  ];

  const handleSelect = (rawValue) => {
    const value = rawValue && rawValue.slice(0, 10);
    if (!value) return;
    // ensure it's within allowed range
    if (value < formatYMD(today) || value > formatYMD(tomorrow)) return;
    // prevent selecting today after cutoff
    if (isAfterCutoff && value === formatYMD(today)) return;
    dispatch(updateOrderFormField('selectedDate', value));
    dispatch(clearOrderFormError('date'));
    
    // If selecting tomorrow, ensure mealType is set so Next button is enabled
    if (value === formatYMD(tomorrow)) {
      dispatch(updateOrderFormField('mealType', 'lunch'));
      dispatch(clearOrderFormError('meal'));
    }
  };

  // Normalize selectedDate for reliable comparisons (some codepaths may include time)
  const normalizedSelected = normalizeToYMD(selectedDate);
  const [localSelected, setLocalSelected] = useState(() => {
    const todayYMD = formatYMD(today);
    const tomorrowYMD = formatYMD(tomorrow);
    return (isAfterCutoff && (!normalizedSelected || normalizedSelected === todayYMD))
      ? tomorrowYMD
      : normalizedSelected;
  });

  useEffect(() => {
    setLocalSelected(normalizedSelected);
  }, [normalizedSelected]);

  // If it's past cutoff, auto-select tomorrow and prevent today selection
  useEffect(() => {
    if (isAfterCutoff) {
      const todayYMD = formatYMD(today);
      const tomorrowYMD = formatYMD(tomorrow);
      if (normalizedSelected === todayYMD || !normalizedSelected) {
        setLocalSelected(tomorrowYMD);
        dispatch(updateOrderFormField('selectedDate', tomorrowYMD));
        dispatch(updateOrderFormField('mealType', 'lunch'));
        dispatch(clearOrderFormError('meal'));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAfterCutoff, normalizedSelected]);

  // Styles object with THEME colors
  const styles = {
    selectedButtonStyle: {
      borderColor: THEME.primary.main,
      background: `linear-gradient(180deg, ${THEME.primary.light}, ${THEME.primary.background})`
    }
  };


  return (
    <div className='order-section step-content'>
      <div className='date-picker-section'>
            <div className='date-options'>
          {options.map(opt => {
            const disabled = isAfterCutoff && opt.key === 'today';
            return (
            <button
              key={opt.key}
              type='button'
              disabled={disabled}
              className={`date-option ${localSelected === opt.value ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
              onClick={() => {
                handleSelect(opt.value);
                setLocalSelected(opt.value);
              }}
              style={localSelected === opt.value ? styles.selectedButtonStyle : {}}
            >
              <div className='date-option-label'>{opt.label}</div>
              <div className='date-option-sub'>{opt.subtitle}</div>
            </button>
          )})}
        </div>

        {isAfterCutoff && localSelected !== formatYMD(tomorrow) && (
          <div className='error-message'>Today's orders are now closed after 5:30 PM.<br />Place your order now to enjoy fresh service tomorrow!</div>
        )}

        {localSelected && (
          <div className='selected-date-display'>
            Selected Date: <strong>{new Date(localSelected).toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</strong>
          </div>
        )}

        {dateError && (
          <div className='error-message'>{dateError}</div>
        )}
      </div>
    </div>
  );
};

export default DeliveryDateStep;
